class PredictiveSearch {
  constructor(form) {
    this.form = form;
    this.input = form.querySelector('input[type="search"]');
    this.predictiveSearchResults = form.querySelector('[data-search-suggestions]');
    this.cancelBtn = form.querySelector('[data-search-cancel]');
    this.clearBtn = form.querySelector('[data-search-clear]');

    if (!this.input || !this.predictiveSearchResults) return;

    this.setupEventListeners();
    
    // LRU Cache implementation (limit: 30)
    this.cache = new Map();
    this.maxCacheSize = 30;
    
    this.abortController = null;
    this.debounceTimeout = null;

    // Load configs from attributes
    this.enableRecent = this.form.getAttribute('data-enable-recent') !== 'false';
    const trendingAttr = this.form.getAttribute('data-trending-searches') || 'Pools, Soft Toys, Arts & Crafts, Stationery';
    this.trendingSearches = trendingAttr.split(',').map(s => s.trim()).filter(s => s);

    // Load recent searches from localStorage
    this.recentSearches = this.enableRecent ? this.loadRecentSearches() : [];
  }

  setupEventListeners() {
    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    
    // Keyboard accessibility
    this.form.addEventListener('keyup', this.onKeyup.bind(this));
    this.form.addEventListener('keydown', this.onKeydown.bind(this));

    // Close when clicking outside
    document.addEventListener('click', this.onClickOutside.bind(this));

    if (this.cancelBtn) {
      this.cancelBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
        this.input.value = '';
        this.toggleClearBtn();
        this.input.blur();
      });
    }

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.input.value = '';
        this.input.focus();
        this.toggleClearBtn();
        this.showTrendingAndRecent();
      });
    }
  }

  toggleClearBtn() {
    if (this.clearBtn) {
      this.clearBtn.style.display = this.input.value.trim().length > 0 ? 'flex' : 'none';
    }
  }

  onInput() {
    this.toggleClearBtn();
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.showTrendingAndRecent();
      return;
    }

    this.showLoadingSkeleton();

    if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(() => {
      this.getSearchResults(query);
    }, 250);
  }

  onFocus() {
    this.toggleClearBtn();
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.showTrendingAndRecent();
    } else {
      this.getSearchResults(query);
    }
  }

  onClickOutside(e) {
    if (window.innerWidth <= 768 && this.form.classList.contains('is-active')) {
      return;
    }
    if (!this.form.contains(e.target)) {
      this.close();
    }
  }

  close() {
    this.form.classList.remove('is-active');
    document.body.classList.remove('ht-search-overlay-open');
    this.predictiveSearchResults.style.display = 'none';
    this.predictiveSearchResults.removeAttribute('aria-expanded');
    this.input.removeAttribute('aria-activedescendant');
    this.removeHighlight();
  }

  open() {
    this.form.classList.add('is-active');
    if (window.innerWidth <= 768) {
      document.body.classList.add('ht-search-overlay-open');
    }
    this.predictiveSearchResults.style.display = 'block';
    this.predictiveSearchResults.setAttribute('aria-expanded', 'true');
  }

  showLoadingSkeleton() {
    this.open();
    this.predictiveSearchResults.innerHTML = `
      <div class="predictive-search-skeleton">
        <div class="skeleton-header"></div>
        <div class="skeleton-item"></div>
        <div class="skeleton-item"></div>
        <div class="skeleton-item"></div>
      </div>
    `;
  }

  showTrendingAndRecent() {
    this.open();
    const isAr = document.documentElement.lang === 'ar';
    let html = '';

    // 1. Recent Searches
    if (this.enableRecent && this.recentSearches.length > 0) {
      html += `
        <div class="predictive-search__group">
          <h3 class="predictive-search__heading">${isAr ? 'البحوث الأخيرة' : 'Recent Searches'}</h3>
          <ul class="predictive-search__list" role="listbox">
            ${this.recentSearches.map((term, index) => `
              <li class="predictive-search__list-item" role="option" id="option-recent-${index}">
                <a href="/search?q=${encodeURIComponent(term)}" class="predictive-search__item predictive-search__item--query" data-search-term="${term.replace(/"/g, '&quot;')}">
                  <svg class="icon icon-history" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                  <span>${this.escapeHTML(term)}</span>
                </a>
              </li>
            `).join('')}
          </ul>
        </div>
      `;
    }

    // 2. Trending Searches
    if (this.trendingSearches.length > 0) {
      html += `
        <div class="predictive-search__group">
          <h3 class="predictive-search__heading">${isAr ? 'البحث الشائع' : 'Trending Searches'}</h3>
          <ul class="predictive-search__list" role="listbox">
            ${this.trendingSearches.map((term, index) => `
              <li class="predictive-search__list-item" role="option" id="option-trending-${index}">
                <a href="/search?q=${encodeURIComponent(term)}" class="predictive-search__item predictive-search__item--query" data-search-term="${term.replace(/"/g, '&quot;')}">
                  <svg class="icon icon-trending" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>
                  <span>${this.escapeHTML(term)}</span>
                </a>
              </li>
            `).join('')}
          </ul>
        </div>
      `;
    }

    if (!html) {
      this.close();
      return;
    }

    this.predictiveSearchResults.innerHTML = html;
    this.attachSuggestionAnalytics();
  }

  getSearchResults(query) {
    const cacheKey = query.toLowerCase();
    
    // Read from LRU Cache
    if (this.cache.has(cacheKey)) {
      const cachedHTML = this.cache.get(cacheKey);
      // Move to end to mark as most recently used
      this.cache.delete(cacheKey);
      this.cache.set(cacheKey, cachedHTML);
      this.renderSearchResults(cachedHTML);
      return;
    }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const localePrefix = window.location.pathname.includes('/en-in') ? '/en-in' : (window.location.pathname.includes('/ar-in') ? '/ar-in' : (window.location.pathname.includes('/en') ? '/en' : (window.location.pathname.includes('/ar') ? '/ar' : '')));
    const url = `${localePrefix}/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,collection,query&section_id=predictive-search`;

    fetch(url, { signal: this.abortController.signal })
      .then(response => {
        if (!response.ok) throw new Error('Search suggestions request failed');
        return response.text();
      })
      .then(text => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const resultsMarkup = doc.getElementById('predictive-search-results');
        
        if (resultsMarkup) {
          const html = resultsMarkup.outerHTML;
          
          // Write to LRU Cache
          if (this.cache.size >= this.maxCacheSize) {
            // Delete oldest key (first element in Map keys)
            const oldestKey = this.cache.keys().next().value;
            this.cache.delete(oldestKey);
          }
          this.cache.set(cacheKey, html);

          this.renderSearchResults(html);
        } else {
          this.showError();
        }
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          console.error('Predictive search error:', error);
          this.showError();
        }
      });
  }

  renderSearchResults(html) {
    this.open();
    this.predictiveSearchResults.innerHTML = html;
    this.attachSuggestionAnalytics();
  }

  attachSuggestionAnalytics() {
    this.predictiveSearchResults.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', (e) => {
        const queryTerm = link.getAttribute('data-search-term') || this.input.value.trim();
        
        // Save query to recent searches
        if (this.enableRecent) {
          this.saveRecentSearch(queryTerm);
        }

        // Emit Custom Event for Analytics
        const eventDetail = {
          term: queryTerm,
          href: link.getAttribute('href'),
          type: link.classList.contains('predictive-search__item--product') ? 'product' : (link.classList.contains('predictive-search__item--collection') ? 'collection' : 'query')
        };
        this.form.dispatchEvent(new CustomEvent('predictive_search:selected', {
          bubbles: true,
          detail: eventDetail
        }));
      });
    });
  }

  showError() {
    this.open();
    const isAr = document.documentElement.lang === 'ar';
    this.predictiveSearchResults.innerHTML = `
      <div class="predictive-search__error">
        <p>${isAr ? 'عذرًا، تعذر تحميل الاقتراحات. يرجى المحاولة مرة أخرى.' : 'Unable to load suggestions. Please try again.'}</p>
      </div>
    `;
  }

  onKeydown(e) {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault(); // Stop body scrolling
    }
  }

  onKeyup(e) {
    if (e.key === 'Escape') {
      this.close();
      this.input.blur();
      return;
    }

    const items = Array.from(this.predictiveSearchResults.querySelectorAll('.predictive-search__list-item'));
    if (items.length === 0) return;

    let highlightedIndex = items.findIndex(item => item.classList.contains('highlighted'));

    if (e.key === 'ArrowDown') {
      this.removeHighlight();
      highlightedIndex = (highlightedIndex + 1) % items.length;
      items[highlightedIndex].classList.add('highlighted');
      items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      this.input.setAttribute('aria-activedescendant', items[highlightedIndex].id || `option-${highlightedIndex}`);
    } else if (e.key === 'ArrowUp') {
      this.removeHighlight();
      highlightedIndex = (highlightedIndex - 1 + items.length) % items.length;
      items[highlightedIndex].classList.add('highlighted');
      items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      this.input.setAttribute('aria-activedescendant', items[highlightedIndex].id || `option-${highlightedIndex}`);
    } else if (e.key === 'Enter') {
      if (highlightedIndex !== -1) {
        e.preventDefault();
        const link = items[highlightedIndex].querySelector('a');
        if (link) {
          link.click();
        }
      } else {
        if (this.enableRecent) {
          this.saveRecentSearch(this.input.value.trim());
        }
      }
    }
  }

  removeHighlight() {
    const highlighted = this.predictiveSearchResults.querySelector('.predictive-search__list-item.highlighted');
    if (highlighted) {
      highlighted.classList.remove('highlighted');
    }
  }

  loadRecentSearches() {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return [];
      const data = window.localStorage.getItem('ht_recent_searches');
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  saveRecentSearch(term) {
    if (!term || term.length < 2) return;
    try {
      let searches = this.loadRecentSearches();
      searches = searches.filter(s => s.toLowerCase() !== term.toLowerCase());
      searches.unshift(term);
      searches = searches.slice(0, 5); // Keep up to 5 items
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('ht_recent_searches', JSON.stringify(searches));
      }
      this.recentSearches = searches;
    } catch (e) {}
  }

  escapeHTML(str) {
    return str.replace(/[&<>'"]/g, 
      tag => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[tag] || tag)
    );
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const forms = document.querySelectorAll('[data-predictive-search-form]');
  forms.forEach(form => {
    new PredictiveSearch(form);
  });
});
